import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message, Spin, TreeSelect, TreeSelectProps } from 'antd';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import { FormInstance } from '@ant-design/pro-components';
import { SolicitorFirmWithSolicitors } from '@api/btlApplicationForm/types';

type AutocompleteTreeProps = {
  value?: string;
  form: FormInstance;
  disabled?: boolean;
  onChange?: (value: string) => void;
};

const formatTreeData = (firms: SolicitorFirmWithSolicitors[]) => {
  return firms.map(firm => ({
    title: (
      <>
        <div className='font-semibold'>{firm.name}</div>
        <div className='text-xs text-gray-500'>{firm.registeredName || ''}</div>
      </>
    ),
    label: `${firm.name}${firm.registeredName ? ` (${firm.registeredName})` : ''}`,
    value: firm.id,
    children: firm.solicitors.map(solicitor => ({
      title: `${solicitor.firstName} ${solicitor.lastName}`,
      label: `${solicitor.firstName} ${solicitor.lastName}`,
      value: `${firm.id}-${solicitor.id}`,
    })),
  }));
};

const AutocompleteTree: React.FC<AutocompleteTreeProps> = ({ form, disabled, value, onChange }) => {
  const [treeData, setTreeData] = useState<TreeSelectProps['treeData']>([]);
  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<{ value: string; label: React.ReactNode }>();
  const searchTimeoutRef = useRef<number>();

  const loadInitialValue = useCallback(async (initialValue: string) => {
    const [solicitorFirmId, solicitorId] = initialValue.split('-');
    if (!solicitorFirmId) return;

    setLoading(true);
    try {
      const firm = await btlApplicationFormApi.getSolicitorFirmById(solicitorFirmId);
      const solicitor = firm.solicitors.find(s => s.id === solicitorId);

      const label = solicitor
        ? `${solicitor.firstName} ${solicitor.lastName}`
        : `${firm.name}${firm.registeredName ? ` (${firm.registeredName})` : ''}`;

      setSelectedValue({ value: initialValue, label });
      setTreeData(formatTreeData([firm]));
    } catch {
      message.error('Failed to load initial selection');
    } finally {
      setLoading(false);
    }
  }, []);

  const searchSolicitors = useCallback(async (searchTerm: string) => {
    if (!searchTerm) {
      setTreeData([]);
      return;
    }

    setLoading(true);
    try {
      const results = await btlApplicationFormApi.searchSolicitorsByRegisteredName(searchTerm);
      setTreeData(formatTreeData(results));
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        message.error('Search failed');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (searchTerm: string) => {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = window.setTimeout(() => searchSolicitors(searchTerm), 400);
    },
    [searchSolicitors],
  );

  const handleChange = useCallback(
    (val: { value: string; label: React.ReactNode } | undefined) => {
      setSelectedValue(val);
      const newValue = val?.value || '';
      form.setFieldValue('registeredName', newValue);
      onChange?.(newValue);
    },
    [form, onChange],
  );

  useEffect(() => {
    if (value) {
      loadInitialValue(value);
    } else {
      setSelectedValue(undefined);
    }
  }, [value, loadInitialValue]);

  return (
    <TreeSelect
      showSearch
      value={selectedValue}
      style={{ width: 400 }}
      placeholder='Search and select'
      allowClear
      filterTreeNode={false}
      disabled={disabled}
      onSearch={debouncedSearch}
      onChange={handleChange}
      treeData={treeData}
      notFoundContent={loading ? <Spin size='small' /> : undefined}
      treeNodeLabelProp='label'
      labelInValue
      loading={loading}
    />
  );
};

export default AutocompleteTree;
